import { memo, useMemo } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import Pill from './Pill';
import { sectionMap } from './CustomAlertsFilterData';
import { normalizeFilterLogic } from './filterLogic';

const aiAlertIds = ['processing', 'needs_attention', 'resolved_via_ai'];

interface AppliedFilter {
  id: string;
  name?: string;
  title?: string;
  logic?: 'AND' | 'OR';
  filterBy?: string;
}

interface PillData {
  key: string;
  label: string;
  opensPanel: boolean;
  onClick?: (event: MouseEvent | KeyboardEvent) => void;
  onRemove: () => void;
  removeAll: boolean;
}

interface AlertFilterPillsProps {
  appliedFilters?: AppliedFilter[];
  removeAppliedFilter: (filter: AppliedFilter | AppliedFilter[]) => void;
  onOpenGroup?: (event: MouseEvent | KeyboardEvent) => void;
}

/**
 * Component for rendering alert filter pills with grouping logic
 *
 * @param appliedFilters - Array of applied filter objects
 * @param removeAppliedFilter - Function to remove a filter or array of filters
 * @param onOpenGroup - Optional callback for opening filter groups
 */
function AlertFilterPills({
  appliedFilters = [],
  removeAppliedFilter,
  onOpenGroup,
}: AlertFilterPillsProps) {
  const byId = useMemo(
    () => new Map((appliedFilters || []).map((f) => [f.id, f])),
    [appliedFilters]
  );

  const buildPill = (
    label: string,
    ids: string[],
    { opensPanel = true }: { opensPanel?: boolean } = {}
  ): PillData => {
    const items = ids
      .map((id) => byId.get(id))
      .filter(Boolean) as AppliedFilter[];
    const logicLabel =
      normalizeFilterLogic(
        items.find((item) => item.filterBy === 'alert_types')?.logic
      ) === 'AND'
        ? 'Alerts (And): '
        : '';
    return {
      key: `${label}__${ids.join('|')}`,
      label: `${logicLabel}${label}`,
      opensPanel,
      onClick: opensPanel ? onOpenGroup : undefined,
      onRemove: () => removeAppliedFilter(items.length > 1 ? items : items[0]),
      removeAll: items.length > 1,
    };
  };

  const pills: PillData[] = [];

  const alertStateIds = sectionMap.alert_state.filter((id) => byId.has(id));
  const hasOpen = alertStateIds.includes('open');
  if (hasOpen && alertStateIds.length > 1) {
    pills.push(
      buildPill(`Open alerts +${alertStateIds.length - 1}`, alertStateIds, {
        opensPanel: true,
      })
    );
  } else {
    alertStateIds.forEach((id) => {
      const f = byId.get(id);
      pills.push(buildPill(f?.name || f?.title || 'Open', [id]));
    });
  }

  const criticalIds = sectionMap.critical_alerts.filter((id) => byId.has(id));
  if (criticalIds.length > 1) {
    pills.push(
      buildPill(`Critical alerts +${criticalIds.length}`, criticalIds, {
        opensPanel: true,
      })
    );
  } else if (criticalIds.length === 1) {
    const f = byId.get(criticalIds[0]);
    pills.push(buildPill(f?.name || f?.title || 'Critical', [criticalIds[0]]));
  }

  const riskIds = sectionMap.at_risk_alerts.filter((id) => byId.has(id));
  if (riskIds.length > 2) {
    pills.push(
      buildPill(`At-risk alerts +${riskIds.length}`, riskIds, {
        opensPanel: true,
      })
    );
  } else {
    riskIds.forEach((id) => {
      const f = byId.get(id);
      pills.push(buildPill(f?.name || f?.title, [id]));
    });
  }

  aiAlertIds.forEach((id) => {
    if (byId.has(id)) {
      const f = byId.get(id);
      pills.push(buildPill(f?.name || f?.title, [id]));
    }
  });

  if (!pills.length) return null;

  return (
    <>
      {pills.map((p) => (
        <Pill
          key={p.key}
          label={p.label}
          onClick={p.onClick}
          onRemove={p.onRemove}
        />
      ))}
    </>
  );
}

export default memo(AlertFilterPills);
